import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Card } from './user';
import { PlanId } from '../utils/paywall';

export type SubscriptionStatus = 'idle' | 'loading' | 'ready' | 'unavailable';

export interface OrganizationSubscription {
  planId: PlanId | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  card: Card | null;
  /** True only while organizationSubscription says the free trial is still running. */
  trialActive: boolean;
  trialEndsAt: string | null;
  /** True when this state came from a successful organizationSubscription response. */
  authoritative: boolean;
}

interface SubscriptionState extends OrganizationSubscription {
  status: SubscriptionStatus;
}

interface SubscriptionContextValue extends SubscriptionState {
  apply: (next: OrganizationSubscription) => void;
  markLoading: () => void;
  markUnavailable: () => void;
  reset: () => void;
}

const emptySubscription: OrganizationSubscription = {
  planId: null,
  cancelAtPeriodEnd: false,
  currentPeriodEnd: null,
  card: null,
  trialActive: false,
  trialEndsAt: null,
  authoritative: false,
};

export function formatSubscriptionDate(iso?: string | null): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString();
}

/** Blocking plan prompt. A failed or missing subscription query must not set this. */
export function organizationNeedsPlan(input: {
  oppText: boolean;
  wpAdmin?: boolean | null;
  status: SubscriptionStatus;
  authoritative: boolean;
  planId: PlanId | null;
  trialActive: boolean;
}): boolean {
  return input.oppText
    && input.status === 'ready'
    && input.authoritative
    && !input.wpAdmin
    && !input.planId
    && !input.trialActive;
}

const SubscriptionContext = React.createContext<SubscriptionContextValue>({
  status: 'idle',
  ...emptySubscription,
  apply: () => undefined,
  markLoading: () => undefined,
  markUnavailable: () => undefined,
  reset: () => undefined,
});

export function normalizeSubscriptionPlan(value?: string | null): PlanId | null {
  if (value === 'practice' || value === 'clinic' || value === 'system') {
    return value;
  }
  return null;
}

export function SubscriptionProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, setState] = useState<SubscriptionState>({
    status: 'idle',
    ...emptySubscription,
  });

  const apply = useCallback((next: OrganizationSubscription) => {
    setState((prev) => {
      const sameCard = prev.card?.last4 === next.card?.last4
        && prev.card?.expMonth === next.card?.expMonth
        && prev.card?.expYear === next.card?.expYear;
      if (
        prev.status === 'ready'
        && prev.planId === next.planId
        && prev.cancelAtPeriodEnd === next.cancelAtPeriodEnd
        && prev.currentPeriodEnd === next.currentPeriodEnd
        && prev.trialActive === next.trialActive
        && prev.trialEndsAt === next.trialEndsAt
        && prev.authoritative === next.authoritative
        && sameCard
      ) {
        return prev;
      }
      return { status: 'ready', ...next };
    });
  }, []);

  const markLoading = useCallback(() => {
    setState((prev) => (prev.status === 'idle' ? { ...prev, status: 'loading' } : prev));
  }, []);

  const markUnavailable = useCallback(() => {
    setState((prev) => (prev.status === 'ready' ? prev : { ...prev, status: 'unavailable' }));
  }, []);

  const reset = useCallback(() => {
    setState({ status: 'idle', ...emptySubscription });
  }, []);

  const value = useMemo(
    () => ({ ...state, apply, markLoading, markUnavailable, reset }),
    [state, apply, markLoading, markUnavailable, reset],
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription(): SubscriptionContextValue {
  return useContext(SubscriptionContext);
}
